import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Checkbox, Collapse, Stack, TableBody, TableCell, TableRow } from '@mui/material';

import GenericTableActions from './GenericTableActions';
import { classes } from './styles';
import { handleScrollableEnter, handleScrollableExit, handleSelectOne } from './tableHelper';
import { DARK_GREY, WHITE } from '../../../ThemeContext/constants';
import ComponentErrorBoundary from '../ComponentErrorBoundary';

import type { SxProps } from '@mui/material';
import type { Dispatch, FC, MutableRefObject, RefObject } from 'react';

import type { ActionListItem } from './GenericTableActions';
import type {
  GenericTableData,
  TableActionItemAction,
  TableScrollAction,
  TableSelectionAction,
  TSortableColumn,
  TTableState
} from './types';

interface IGenericTableBodyProps<T> {
  tableState: TTableState<T, TSortableColumn[]>;
  dispatch: Dispatch<TableActionItemAction<T> | TableSelectionAction<T> | TableScrollAction>;
  items: T[];
  TableRows: FC<{
    column: TSortableColumn;
    item: T;
    tableHovered: boolean;
    dispatch?: Dispatch<TableActionItemAction<T> | TableSelectionAction<T>>;
    onExpand?: () => void;
    isExpanded?: boolean;
  }>;
  TableActionList?: ActionListItem[];
  allowMultipleSelection?: boolean;
  ExpandedContent?: FC<{ item: T }>;
  expandableCellIds?: string[];
  hasStickyColumnBeforeActions?: boolean;
  customRowStyles?: Record<string, SxProps>;
  tableRef: RefObject<HTMLDivElement>;
  titleCellRef: RefObject<HTMLTableCellElement>;
  actionCellRef: RefObject<HTMLTableCellElement>;
  hoverTimeoutRef: MutableRefObject<NodeJS.Timeout | null>;
  isHoveringRef: MutableRefObject<boolean>;
}

const GenericTableBody = <T extends GenericTableData>({
  tableState,
  dispatch,
  items,
  TableRows,
  TableActionList,
  allowMultipleSelection = true,
  ExpandedContent,
  expandableCellIds = [],
  hasStickyColumnBeforeActions,
  customRowStyles,
  tableRef,
  titleCellRef,
  actionCellRef,
  hoverTimeoutRef,
  isHoveringRef
}: IGenericTableBodyProps<T>) => {
  const localHoverTimeoutRef = hoverTimeoutRef;
  const localIsHoveringRef = isHoveringRef;
  const [expandedItemIds, setExpandedItemIds] = useState<Set<number>>(new Set());
  const [animatingOutIds, setAnimatingOutIds] = useState<Set<number>>(new Set());
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const allItemIds = useMemo(() => items.map((item) => item.id), [items]);

  useEffect(() => {
    const el = tableRef.current;
    if (!el) return undefined;
    setContainerWidth(el.clientWidth);
    const observer = new ResizeObserver(() => setContainerWidth(el.clientWidth));
    observer.observe(el);
    return () => observer.disconnect();
  }, [tableRef]);

  const handleRowExpand = useCallback((item: T) => {
    setExpandedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(item.id)) {
        next.delete(item.id);
        setAnimatingOutIds((a) => new Set(a).add(item.id));
      } else {
        next.add(item.id);
        setAnimatingOutIds((a) => {
          const b = new Set(a);
          b.delete(item.id);
          return b;
        });
      }
      return next;
    });
  }, []);

  const handleCollapseExited = useCallback((itemId: number) => {
    setAnimatingOutIds((prev) => {
      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });
  }, []);

  const clearHoverTimeout = useCallback(() => {
    if (localHoverTimeoutRef.current) {
      clearTimeout(localHoverTimeoutRef.current);
      localHoverTimeoutRef.current = null;
    }
  }, [localHoverTimeoutRef]);

  const handleRowMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement>) => {
      if (!titleCellRef.current || !actionCellRef.current) return;

      const titleRect = titleCellRef.current.getBoundingClientRect();
      const actionRect = actionCellRef.current.getBoundingClientRect();
      const mouseX = event.clientX;

      if (mouseX > titleRect.right && mouseX < actionRect.left) {
        clearHoverTimeout();
        localIsHoveringRef.current = true;
        handleScrollableEnter(tableRef, dispatch);
      } else {
        localIsHoveringRef.current = false;
        handleScrollableExit(dispatch);
      }
    },
    [titleCellRef, actionCellRef, clearHoverTimeout, localIsHoveringRef, tableRef, dispatch]
  );

  const handleRowMouseLeave = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement>) => {
      const relatedTarget = event.relatedTarget as HTMLElement;

      const isArrowElement =
        relatedTarget?.id === 'left-arrow' || relatedTarget?.id === 'right-arrow';
      const isInsideArrow =
        typeof relatedTarget?.closest === 'function' &&
        (relatedTarget.closest('#left-arrow') !== null ||
          relatedTarget.closest('#right-arrow') !== null);

      if (relatedTarget && (isArrowElement || isInsideArrow)) {
        return;
      }

      localIsHoveringRef.current = false;
      clearHoverTimeout();
      localHoverTimeoutRef.current = setTimeout(() => {
        if (!localIsHoveringRef.current) {
          handleScrollableExit(dispatch);
        }
        localHoverTimeoutRef.current = null;
      }, 400);
    },
    [dispatch, clearHoverTimeout, localHoverTimeoutRef, localIsHoveringRef]
  );

  return (
    <TableBody>
      {items.map((item) => (
        <Fragment key={item.id}>
          <TableRow
            onMouseEnter={handleRowMouseEnter}
            onMouseLeave={handleRowMouseLeave}
            sx={{
              ...(ExpandedContent &&
                (expandedItemIds.has(item.id) || animatingOutIds.has(item.id)) && {
                  backgroundColor: expandedItemIds.has(item.id)
                    ? 'rgba(21, 199, 192, 0.08)'
                    : undefined,
                  borderBottom: 'none !important'
                }),
              ...customRowStyles
            }}
          >
            {allowMultipleSelection ? (
              <TableCell sx={{ ...classes.sticky, width: '58px' }}>
                <Checkbox
                  checked={tableState.selectedItemIds.includes(item.id)}
                  id={`select-checkbox-${item.id}`}
                  onChange={(event) => handleSelectOne(event, item, allItemIds, dispatch)}
                  sx={classes.checkbox}
                  slotProps={{
                    input: {
                      'aria-label': `select ${item.name}`
                    }
                  }}
                />
              </TableCell>
            ) : null}
            {tableState.columns.map(
              (column) =>
                column.enabled && (
                  <TableRows
                    key={column.id}
                    column={column}
                    dispatch={dispatch}
                    isExpanded={expandedItemIds.has(item.id)}
                    item={item}
                    tableHovered={tableState.tableHovered}
                    onExpand={
                      expandableCellIds.includes(column.id)
                        ? () => handleRowExpand(item)
                        : undefined
                    }
                  />
                )
            )}
            <TableCell
              ref={actionCellRef}
              onClick={
                ExpandedContent && !TableActionList ? () => handleRowExpand(item) : undefined
              }
              sx={{
                zIndex: '4',
                ...classes.actionsTableCell,
                ...classes.stickyRight,
                ...(tableState.tableHovered &&
                  !hasStickyColumnBeforeActions &&
                  classes.scrollShadowVisible),
                ...(!TableActionList &&
                  ExpandedContent && { minWidth: '60px', width: '60px', cursor: 'pointer' }),
                ...(!TableActionList && !ExpandedContent && { minWidth: '24px', width: '24px' })
              }}
            >
              <Box sx={classes.actionItems}>
                {!!TableActionList && (
                  <GenericTableActions actions={TableActionList} dispatch={dispatch} item={item} />
                )}
                {!TableActionList && !!ExpandedContent && (
                  <TableRows
                    isExpanded={expandedItemIds.has(item.id)}
                    item={item}
                    onExpand={() => handleRowExpand(item)}
                    tableHovered={tableState.tableHovered}
                    column={{
                      id: 'expand',
                      label: '',
                      enabled: true,
                      sortable: false,
                      canToggle: false
                    }}
                  />
                )}
              </Box>
            </TableCell>
          </TableRow>
          {!!ExpandedContent && (
            <TableRow
              sx={{ borderBottom: !expandedItemIds.has(item.id) ? 'none !important' : undefined }}
            >
              <TableCell
                colSpan={tableState.columns.length + (allowMultipleSelection ? 2 : 1)}
                sx={{ padding: '0 !important', border: 'none' }}
              >
                {/*
                 * position: sticky; left: 0 is on this inner div, NOT the <td>.
                 * - The <td> has no overflow, so nothing between this div and the
                 *   TableContainer scroll container blocks sticky.
                 * - Collapse (overflow:hidden) is a *descendant*, so it cannot
                 *   prevent this div from sticking.
                 * - overflow: hidden here clips internal content to containerWidth
                 *   without creating a new scroll context for the sticky lookup.
                 */}
                <div
                  style={{
                    position: 'sticky',
                    left: 0,
                    width: containerWidth > 0 ? `${containerWidth}px` : '100%',
                    overflow: 'hidden',
                    backgroundColor: WHITE
                  }}
                >
                  <Collapse
                    unmountOnExit
                    in={expandedItemIds.has(item.id)}
                    onExited={() => handleCollapseExited(item.id)}
                    timeout="auto"
                  >
                    <ComponentErrorBoundary
                      fallback={
                        <Stack sx={{ color: DARK_GREY, fontSize: '14px', padding: '16px 24px' }}>
                          This content couldn&apos;t be displayed.
                        </Stack>
                      }
                    >
                      <ExpandedContent item={item} />
                    </ComponentErrorBoundary>
                  </Collapse>
                </div>
              </TableCell>
            </TableRow>
          )}
        </Fragment>
      ))}
    </TableBody>
  );
};

export default GenericTableBody;
